import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogTrigger, DialogClose } from '@/components/ui/dialog'
import { toast } from 'sonner'
import { runDmlScript, getDmlScripts } from '@/api/rewards-grants'
import { Icons } from '@/theme/Images'
import { Divider } from '@/components/divider'
import RulesDialogHeader from '../rules-page/dialog-header/rules-dialog-header'
import SelectBlock from '../rules-page/select-block'
import Checkbox from '@/components/check-box'
import { useFormik } from 'formik'
import * as Yup from 'yup'

const RunScript = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [scripts, setScripts] = useState([])
  const [isLoadingScripts, setIsLoadingScripts] = useState(false)

  const validationSchema = Yup.object({
    scriptName: Yup.string().required('Script Name is required'),
  })

  const formik = useFormik({
    initialValues: {
      scriptName: '',
      dryRun: true,
    },
    validationSchema,
    onSubmit: async () => {
      if (isLoading) return
      setIsLoading(true)

      try {
        await runDmlScript({
          scriptName: formik.values.scriptName,
          dryRun: formik.values.dryRun,
        })

        setIsOpen(false)
        toast.success('Script execution started', {
          description: `Script ${formik.values.scriptName} is now running${formik.values.dryRun ? ' (dry run)' : ''}.`,
        })

        // Reset form
        formik.resetForm()
      } catch (error) {
        console.error(error)
        const errorMessage = error?.message || error?.data?.message || 'Failed to run script'
        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
      }
    },
  })

  // Fetch scripts when modal opens
  useEffect(() => {
    if (isOpen && scripts.length === 0) {
      setIsLoadingScripts(true)
      getDmlScripts()
        .then(response => {
          setScripts(response.scripts || [])
        })
        .catch(error => {
          console.error('Failed to fetch DML scripts:', error)
          toast.error('Failed to load available scripts')
        })
        .finally(() => {
          setIsLoadingScripts(false)
        })
    }
  }, [isOpen, scripts.length])

  // Reset state when modal closes
  const handleOpenChange = open => {
    setIsOpen(open)
    if (open) {
      // Reset form when opening the modal
      formik.resetForm()
    } else {
      // Reset form when closing
      formik.resetForm()
    }
  }

  // Intercept cancel: close modal
  const handleCancel = e => {
    e?.preventDefault?.()
    setIsOpen(false)
  }

  const renderForm = () => {
    const scriptOptions = scripts.map(script => ({
      value: script,
      label: script,
    }))

    return (
      <div>
        <div className="w-full">
          <SelectBlock
            label="Script Name*"
            id="scriptName"
            placeholder={isLoadingScripts ? 'Loading scripts...' : 'Select a script'}
            formik={formik}
            options={scriptOptions}
            showValidation={true}
            description="Select the DML script to run."
          />
        </div>
        <div className="w-full mt-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              isChecked={formik.values.dryRun}
              onClick={() => formik.setFieldValue('dryRun', !formik.values.dryRun)}
            />
            <label
              htmlFor="dryRun"
              className="text-sm font-medium text-[--gray-700] cursor-pointer"
              onClick={() => formik.setFieldValue('dryRun', !formik.values.dryRun)}
            >
              Dry Run
            </label>
          </div>
          <p className="text-sm text-[--gray-600] font-normal mt-1">
            If enabled, the script will run without making any database changes.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button
            className="bg-brand-600 hover:bg-brand-700 md:w-auto h-[36px] text-sm text-white"
            onClick={() => setIsOpen(true)}
          >
            <img src={Icons.plusIcon} alt="icon" height={11.67} width={11.67} />
            <span className="font-semibold text-sm ml-2">Run Script</span>
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-[880px] h-[90vh] flex flex-col">
          <RulesDialogHeader
            title="Run Script"
            description="Execute a DML script to perform data migrations or backfills."
            icon={Icons.arrowCircleBrokenRight}
          />
          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 min-h-0">
            <form onSubmit={formik.handleSubmit} className="min-h-[300px]">
              {renderForm()}
            </form>
          </div>
          <div className="ml-[-26px] w-[106%]">
            <Divider />
          </div>
          <DialogFooter className="pt-2">
            <DialogClose asChild>
              <Button
                variant="outline"
                className="w-full md:w-[410px] h-[44px] text-[--gray-700]"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="text-white w-full md:w-[410px] h-[44px]"
              onClick={formik.handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Running...' : 'Run'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RunScript
